import type { PlainWorkflow, PlainWorkflowExecution, PlainWorkflowTrigger } from "@/queries/automation-queries";
import {
    WorkflowExecutionAction_Status_StepStatus_StepPhase,
    WorkflowExecutionPhase,
    WorkflowTrigger_PullRequestEvent,
} from "gitpod-next-api/gitpod/v1/workflow_pb";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import type { OrganizationMember } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { AgentCodeContext } from "gitpod-next-api/gitpod/v1/agent_pb";

export const workflowsUrl = () => "/automations/";
export const workflowDetailsUrl = ({ id }: { id: string }) => `/automations/${id}`;
export const workflowDetailsEditUrl = ({ id }: { id: string }) => `/automations/${id}/edit`;
export const workflowExecutionsUrl = ({ id }: { id: string }) => `/automations/executions/${id}`;
export const workflowExecutionActionUrl = ({ actionId, executionId }: { actionId: string; executionId: string }) =>
    `/automations/executions/${executionId}/actions/${actionId}`;

export const workflowName = (workflow?: PlainWorkflow) => workflow?.metadata?.name || workflow?.id;

export const workflowExecutionPhaseLabel = (workflowExecution: PlainWorkflowExecution) => {
    switch (workflowExecution.status?.phase) {
        case WorkflowExecutionPhase.UNSPECIFIED:
            return "Unspecified";
        case WorkflowExecutionPhase.PENDING:
            return "Pending";
        case WorkflowExecutionPhase.RUNNING:
            return "Running";
        case WorkflowExecutionPhase.STOPPING:
            return "Stopping";
        case WorkflowExecutionPhase.STOPPED:
            return "Stopped";
        case WorkflowExecutionPhase.DELETING:
            return "Deleting";
        case WorkflowExecutionPhase.DELETED:
            return "Deleted";
        case WorkflowExecutionPhase.COMPLETED:
            return hasFailedActions(workflowExecution) ? "Failed" : "Completed";
        default:
            return "Unknown";
    }
};

export const workflowExecutionActionStepPhaseLabel = (phase?: WorkflowExecutionAction_Status_StepStatus_StepPhase) => {
    switch (phase) {
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.UNSPECIFIED:
            return "Unspecified";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.FAILED:
            return "Failed";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.PENDING:
            return "Pending";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.RUNNING:
            return "Running";
        case WorkflowExecutionAction_Status_StepStatus_StepPhase.DONE:
            return "Done";
        default:
            return "Unknown";
    }
};

export const workflowCreatedDate = (workflow?: PlainWorkflow): Date | undefined => {
    const ts = workflow?.metadata?.createdAt;
    if (!ts) {
        return undefined;
    }
    return new Timestamp(ts).toDate();
};

export const workflowCreatorName = (
    workflow?: PlainWorkflow,
    members?: PlainMessage<OrganizationMember>[],
): string | undefined => {
    const creatorId = workflow?.metadata?.creator?.id;
    if (!creatorId || !members) {
        return undefined;
    }
    const memberLookup = Object.fromEntries(members.map((m) => [m.userId, m]));
    return memberLookup[creatorId]?.fullName;
};

export const getMostRecentExecution = (executions?: PlainWorkflowExecution[]): PlainWorkflowExecution | undefined => {
    if (!executions || executions.length === 0) {
        return undefined;
    }
    return executions
        .sort((a, b) => Number(b?.metadata?.startedAt?.seconds || 0) - Number(a?.metadata?.startedAt?.seconds || 0))
        .at(0);
};

export const triggerName = (trigger?: PlainWorkflowTrigger): string | null => {
    if (!trigger) return null;

    switch (trigger.trigger.case) {
        case "manual":
            return "Manual";
        case "pullRequest":
            return "Pull Request";
        default:
            return "Unknown";
    }
};

export const pullRequestEventName = (event: WorkflowTrigger_PullRequestEvent): string => {
    switch (event) {
        case WorkflowTrigger_PullRequestEvent.APPROVED:
            return "Approved";
        case WorkflowTrigger_PullRequestEvent.CLOSED:
            return "Closed";
        case WorkflowTrigger_PullRequestEvent.MERGED:
            return "Merged";
        case WorkflowTrigger_PullRequestEvent.OPENED:
            return "Opened";
        case WorkflowTrigger_PullRequestEvent.UPDATED:
            return "Updated";
        case WorkflowTrigger_PullRequestEvent.UNSPECIFIED:
        default:
            return "Unspecified";
    }
};

export const getProjectIDFromContext = (context?: PlainMessage<AgentCodeContext>): string | null => {
    switch (context?.context.case) {
        case "projectId":
            return context.context.value;
        default:
            return null;
    }
};

export const canCancel = (_execution: PlainWorkflowExecution): boolean => {
    // TODO(AGENT-1092): Implement proper logic later.
    return true;
};

export const isWorkflowRunning = (executions?: PlainWorkflowExecution[]): boolean => {
    const mostRecentExecution = getMostRecentExecution(executions);
    return mostRecentExecution?.status?.phase === WorkflowExecutionPhase.RUNNING;
};

export const isWorkflowCompleted = (executions?: PlainWorkflowExecution[]): boolean => {
    const mostRecentExecution = getMostRecentExecution(executions);
    return mostRecentExecution?.status?.phase === WorkflowExecutionPhase.COMPLETED;
};

export const hasFailedActions = (execution: PlainWorkflowExecution): boolean => {
    return (execution.status?.failedActionCount ?? 0) > 0;
};
